import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  FileCode,
  AlertCircle,
  ExternalLink,
  ArrowRight,
  Activity
} from 'lucide-react';
import { dashboardApi } from '../../services/iso20022Api';
import {
  formatDate,
  getStatusBadgeClass,
  getDirectionBadgeClass,
  copyToClipboard,
  downloadFile
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const StatusRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [statusRequest, setStatusRequest] = useState(null);
  const [relatedMessages, setRelatedMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);
  const [selectedXml, setSelectedXml] = useState(null);

  useEffect(() => {
    loadStatusRequest();
    loadRelatedMessages();
  }, [id]);

  const loadStatusRequest = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getStatusRequestById(id);
      setStatusRequest(data);
    } catch (err) {
      console.error('Failed to load status request:', err);
      setError('Status request not found');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedMessages = async () => {
    try {
      const data = await dashboardApi.getStatusRequestRelatedMessages(id);
      setRelatedMessages(data);
    } catch (err) {
      console.error('Failed to load related messages:', err);
      setRelatedMessages({ originalPayment: null, statusResponses: [] });
    }
  };

  const handleCopy = async (field, value) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleDownloadXml = () => {
    const xmlToDownload = statusRequest?.rawXml || statusRequest?.signedXml;
    if (xmlToDownload) {
      const fileName = `status-request-${statusRequest.id}-pacs028.xml`;
      downloadFile(xmlToDownload, fileName, 'application/xml');
    }
  };

  const handleViewXml = (xml, title, fileName) => {
    setSelectedXml({ xml, title, fileName });
    setXmlModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading status request...</p>
        </div>
      </div>
    );
  }

  if (error || !statusRequest) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-danger-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{error || 'Status Request Not Found'}</h2>
        <button onClick={() => navigate('/iso20022/status-requests')} className="btn-primary mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Status Requests
        </button>
      </div>
    );
  }

  const CopyableField = ({ label, value, field }) => (
    <div>
      <dt className="text-sm font-medium text-gray-500 mb-1">{label}</dt>
      <dd className="flex items-center space-x-2">
        <span className="text-sm font-mono text-gray-900">{value || 'N/A'}</span>
        {value && (
          <button
            onClick={() => handleCopy(field, value)}
            className="text-gray-400 hover:text-primary-600 transition-colors"
            title="Copy to clipboard"
          >
            {copiedField === field ? (
              <CheckCircle className="w-4 h-4 text-success-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        )}
      </dd>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/status-requests')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Status Request #{statusRequest.id}
            </h1>
            <p className="text-gray-600 mt-1">pacs.028 Payment Status Request</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {(statusRequest.rawXml || statusRequest.signedXml) && (
            <>
              <button
                onClick={() => handleViewXml(
                  statusRequest.rawXml || statusRequest.signedXml,
                  `Status Request XML - #${statusRequest.id}`,
                  `status-request-${statusRequest.id}-pacs028.xml`
                )}
                className="btn-secondary flex items-center space-x-2"
              >
                <FileCode className="w-4 h-4" />
                <span>View XML</span>
              </button>
              <button
                onClick={handleDownloadXml}
                className="btn-secondary flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download XML</span>
              </button>
            </>
          )}
          <button
            onClick={loadStatusRequest}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Request Status</h2>
        </div>
        <div className="card-body">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              {statusRequest.signatureOk === true ? (
                <CheckCircle className="w-8 h-8 text-success-600" />
              ) : statusRequest.signatureOk === false ? (
                <XCircle className="w-8 h-8 text-danger-600" />
              ) : (
                <Clock className="w-8 h-8 text-warning-600" />
              )}
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="text-lg font-semibold">
                  <span className={`badge ${getStatusBadgeClass(statusRequest.status)}`}>
                    {statusRequest.status || 'UNKNOWN'}
                  </span>
                </p>
              </div>
            </div>

            <div className="h-12 w-px bg-gray-200"></div>

            <div>
              <p className="text-sm text-gray-600">Direction</p>
              <p className="text-lg font-semibold">
                <span className={`badge ${getDirectionBadgeClass(statusRequest.direction)}`}>
                  {statusRequest.direction}
                </span>
              </p>
            </div>

            <div className="h-12 w-px bg-gray-200"></div>

            <div>
              <p className="text-sm text-gray-600">Signature</p>
              <p className="text-lg font-semibold">
                {statusRequest.signatureOk === true ? (
                  <span className="text-success-600">Valid</span>
                ) : statusRequest.signatureOk === false ? (
                  <span className="text-danger-600">Invalid</span>
                ) : (
                  <span className="text-gray-400">N/A</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message Identification */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Message Identification</h2>
          </div>
          <div className="card-body">
            <dl className="space-y-4">
              <CopyableField label="Message ID" value={statusRequest.msgId} field="msgId" />
              <CopyableField label="Creation Date/Time" value={formatDate(statusRequest.creDtTm, 'PPpp')} field="creDtTm" />
            </dl>
          </div>
        </div>

        {/* Original Transaction Reference */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Original Transaction Reference</h2>
          </div>
          <div className="card-body">
            <dl className="space-y-4">
              <CopyableField label="Original Group Message ID" value={statusRequest.orglGrpInfMsgId} field="orglGrpInfMsgId" />
              <CopyableField label="Original Message Name ID" value={statusRequest.orglGrpInfMsgNmId} field="orglGrpInfMsgNmId" />
              <CopyableField label="Original End-to-End ID" value={statusRequest.orglEndToEndId} field="orglEndToEndId" />
              <CopyableField label="Original Transaction ID" value={statusRequest.orglTxId} field="orglTxId" />
              <CopyableField label="Original Instruction ID" value={statusRequest.orglInstrId} field="orglInstrId" />
              <CopyableField label="Original UETR" value={statusRequest.orglUetr} field="orglUetr" />
            </dl>
          </div>
        </div>

        {/* Instructing Agent */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Instructing Agent</h2>
          </div>
          <div className="card-body">
            <dl className="space-y-4">
              <CopyableField label="BIC" value={statusRequest.instgAgtBic} field="instgAgtBic" />
              <CopyableField label="Clearing System Member ID" value={statusRequest.instgAgtClrSysMmbId} field="instgAgtClrSysMmbId" />
              <CopyableField label="ID" value={statusRequest.instgAgtId} field="instgAgtId" />
              <CopyableField label="Name" value={statusRequest.instgAgtName} field="instgAgtName" />
            </dl>
          </div>
        </div>

        {/* Instructed Agent */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Instructed Agent</h2>
          </div>
          <div className="card-body">
            <dl className="space-y-4">
              <CopyableField label="BIC" value={statusRequest.instdAgtBic} field="instdAgtBic" />
              <CopyableField label="Clearing System Member ID" value={statusRequest.instdAgtClrSysMmbId} field="instdAgtClrSysMmbId" />
              <CopyableField label="ID" value={statusRequest.instdAgtId} field="instdAgtId" />
              <CopyableField label="Name" value={statusRequest.instdAgtName} field="instdAgtName" />
            </dl>
          </div>
        </div>
      </div>

      {/* Signature Details */}
      {statusRequest.signatureOk !== null && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Signature Information</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <CopyableField label="Signature Method" value={statusRequest.signatureMethod} field="signatureMethod" />
              <CopyableField label="Digest Method" value={statusRequest.digestMethod} field="digestMethod" />
              <CopyableField label="Canonicalization Method" value={statusRequest.canonicalizationMethod} field="canonicalizationMethod" />
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Details</dt>
                <dd className="text-sm text-gray-900">{statusRequest.signatureDetails || 'N/A'}</dd>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Related Messages */}
      {relatedMessages && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Related Messages</h2>
            <Activity className="w-5 h-5 text-gray-400" />
          </div>
          <div className="card-body">
            {/* Original Payment */}
            {relatedMessages.originalPayment ? (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                  <ArrowRight className="w-4 h-4 mr-2 text-blue-600" />
                  Original Payment (pacs.008)
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-600">ID</p>
                      <p className="font-mono text-sm text-gray-900">#{relatedMessages.originalPayment.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">End-to-End ID</p>
                      <p className="font-mono text-sm text-gray-900">{relatedMessages.originalPayment.endToEndId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Amount</p>
                      <p className="font-mono text-sm text-gray-900">{relatedMessages.originalPayment.amountCcy}</p>
                    </div>
                    <div className="flex items-center justify-end">
                      <Link
                        to={`/iso20022/transactions/${relatedMessages.originalPayment.id}`}
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center text-sm"
                      >
                        <span>View Details</span>
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Original Payment (pacs.008)</h3>
                <p className="text-sm text-gray-500 italic">No original payment found</p>
              </div>
            )}

            {/* Status Responses */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <ArrowRight className="w-4 h-4 mr-2 text-green-600" />
                Status Responses (pacs.002)
              </h3>
              {relatedMessages.statusResponses && relatedMessages.statusResponses.length > 0 ? (
                <div className="space-y-3">
                  {relatedMessages.statusResponses.map((response, index) => (
                    <div key={response.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-center">
                        <div>
                          <p className="text-xs text-gray-600">ID</p>
                          <p className="font-mono text-sm text-gray-900">#{response.id}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Transaction Status</p>
                          <span className={`badge ${getStatusBadgeClass(response.txSts)}`}>
                            {response.txSts}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Created</p>
                          <p className="text-sm text-gray-900">{formatDate(response.createdAt, 'PPp')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Signature</p>
                          <p className="text-sm">
                            {response.signatureOk ? (
                              <span className="text-success-600">✓ Valid</span>
                            ) : (
                              <span className="text-danger-600">✗ Invalid</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center justify-end space-x-2">
                          {(response.rawXml || response.xmlContent) && (
                            <button
                              onClick={() => handleViewXml(
                                response.rawXml || response.xmlContent,
                                `Status Response XML - #${response.id}`,
                                `status-response-${response.id}-pacs002.xml`
                              )}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100"
                              title="View XML"
                            >
                              <FileCode className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No status responses received yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Timestamps</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Created At</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(statusRequest.createdAt, 'PPpp')}</p>
            </div>
            {statusRequest.receivedAt && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Received At</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(statusRequest.receivedAt, 'PPpp')}</p>
              </div>
            )}
            {statusRequest.signedAt && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Signed At</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(statusRequest.signedAt, 'PPpp')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* XML Viewer Modal */}
      {selectedXml && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => {
            setXmlModalOpen(false);
            setSelectedXml(null);
          }}
          xmlContent={selectedXml.xml}
          fileName={selectedXml.fileName}
          title={selectedXml.title}
        />
      )}
    </div>
  );
};

export default StatusRequestDetail;
